import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { listMailTemplates } from '@/features/preinvest/mailtemplates/api'
import type { MailTemplate } from '@/features/preinvest/mailtemplates/types'
import { toast } from '@/lib/toast'
import { sendContactMail, sendContactMailFromTemplate } from './mail'

/** Same merge-tag pattern as the backend `_MERGE_TAG_RE` and the deal compose. */
const MERGE_TAG_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g

function collectVariableKeys(template: MailTemplate | null): string[] {
  if (!template) return []
  const keys = new Set<string>()
  for (const source of [template.subject, template.body_md]) {
    for (const match of source.matchAll(MERGE_TAG_RE)) {
      keys.add(match[1])
    }
  }
  return Array.from(keys)
}

/** Contact fields that may pre-fill a merge-tag with a matching key. */
function prefillFromContact(
  keys: string[],
  contact: { full_name: string; email: string | null; organisation: string | null },
): Record<string, string> {
  const values: Record<string, string> = {}
  for (const key of keys) {
    if (key === 'contact_name' || key === 'naam') values[key] = contact.full_name
    else if (key === 'email') values[key] = contact.email ?? ''
    else if (key === 'organisation' || key === 'organisatie')
      values[key] = contact.organisation ?? ''
    else values[key] = ''
  }
  return values
}

/**
 * Naive plain-text → HTML wrap: escape and newline→<br>. The backend wraps in
 * the VONK branding; this only needs to survive HTML-escaping so the recipient
 * sees line breaks.
 */
function textToSafeHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return escaped
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
    .join('')
}

/**
 * "E-mail sturen"-dialog voor één contact (spec-48 WP3). Kiest optioneel een
 * `definitief`-template, vult merge-tags voor uit de contactvelden, en post
 * naar de contact-mail endpoints. `to` blijft leeg → backend vult het
 * e-mailadres van het contact.
 */
export function ContactMailDialog({
  open,
  onOpenChange,
  contact,
  onSent,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  contact: { person_id: string; full_name: string; email: string | null; organisation: string | null }
  onSent?: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-4 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>E-mail sturen: {contact.full_name}</DialogTitle>
          <DialogDescription>
            Verstuurd vanuit info@vonk.ventures met de VONK-huisstijl en handtekening.
          </DialogDescription>
        </DialogHeader>
        <MailForm
          contact={contact}
          onCancel={() => onOpenChange(false)}
          onSent={() => {
            onSent?.()
            onOpenChange(false)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

function MailForm({
  contact,
  onCancel,
  onSent,
}: {
  contact: { person_id: string; full_name: string; email: string | null; organisation: string | null }
  onCancel: () => void
  onSent: () => void
}) {
  const [templates, setTemplates] = useState<MailTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [to, setTo] = useState(contact.email ?? '')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [variables, setVariables] = useState<Record<string, string>>({})
  const [sending, setSending] = useState(false)

  useEffect(() => {
    let cancelled = false
    listMailTemplates('definitief')
      .then((rows) => {
        if (!cancelled) setTemplates(rows)
      })
      .catch(() => {
        if (!cancelled) setTemplates([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selectedTemplate = useMemo(
    () => templates.find((row) => row.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  )
  const variableKeys = collectVariableKeys(selectedTemplate)

  function applyTemplate(templateId: string) {
    setSelectedTemplateId(templateId)
    if (!templateId) {
      setVariables({})
      return
    }
    const template = templates.find((row) => row.id === templateId)
    if (!template) return
    setSubject(template.subject)
    setBody(template.body_md)
    setVariables(prefillFromContact(collectVariableKeys(template), contact))
  }

  const canSubmit = subject.trim() !== '' && body.trim() !== '' && !sending

  async function submit() {
    if (!canSubmit) return
    setSending(true)
    try {
      const toList = to
        .split(/[,;]/)
        .map((part) => part.trim())
        .filter(Boolean)
      if (selectedTemplate) {
        await sendContactMailFromTemplate(contact.person_id, {
          template_id: selectedTemplate.id,
          to: toList.length > 0 ? toList : undefined,
          variables,
        })
      } else {
        await sendContactMail(contact.person_id, {
          to: toList.length > 0 ? toList : undefined,
          subject: subject.trim(),
          body_html: textToSafeHtml(body.trim()),
        })
      }
      toast.success('E-mail verstuurd.')
      onSent()
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Versturen mislukt.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {templates.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cm-template">Template</Label>
          <select
            id="cm-template"
            className="h-9 w-full rounded-lg border border-input bg-transparent px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
            value={selectedTemplateId}
            disabled={sending}
            onChange={(event) => applyTemplate(event.target.value)}
          >
            <option value="">Geen (leeg bericht)</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.title}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {variableKeys.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <Label>Variabelen</Label>
          <table className="w-full text-sm">
            <tbody>
              {variableKeys.map((key) => (
                <tr key={key}>
                  <th
                    scope="row"
                    className="w-40 py-1 pr-2 text-left font-mono text-xs text-muted-foreground"
                  >
                    {`{{ ${key} }}`}
                  </th>
                  <td className="py-1">
                    <Input
                      aria-label={`Variabele ${key}`}
                      value={variables[key] ?? ''}
                      disabled={sending}
                      onChange={(event) =>
                        setVariables((current) => ({
                          ...current,
                          [key]: event.target.value,
                        }))
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cm-to">Aan</Label>
        <Input
          id="cm-to"
          value={to}
          disabled={sending}
          placeholder="naam@voorbeeld.nl"
          aria-label="Aan"
          onChange={(event) => setTo(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cm-subject">Onderwerp</Label>
        <Input
          id="cm-subject"
          value={subject}
          disabled={sending}
          aria-label="Onderwerp"
          onChange={(event) => setSubject(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cm-body">Bericht</Label>
        <textarea
          id="cm-body"
          className="min-h-32 w-full resize-y rounded-lg border border-input bg-transparent p-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
          value={body}
          disabled={sending}
          aria-label="Bericht"
          onChange={(event) => setBody(event.target.value)}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuleren
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={!canSubmit}>
          {sending ? 'Bezig…' : 'Versturen'}
        </Button>
      </div>
    </div>
  )
}
